import { Injectable, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { BehaviorSubject, EMPTY, merge, Observable, of, Subject, Subscription } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
} from 'rxjs/operators';

import { BlockPreviewService } from '../../../services/block-preview.service';
import { BlockPreviewRequest, requestKey } from './block-preview-request';
import { BlockPreviewSource } from './block-preview-source';
import {
  BlockPreviewState,
  errorState,
  idleState,
  loadingState,
  readyState,
} from './block-preview-state';

/** Quiet time after the last edit before the preview asks again (task-cms-block-preview.md §5.1). */
export const PREVIEW_DEBOUNCE_MS = 800;

/**
 * The preview panel's logic (#263), one per panel (provided by BlockPreviewComponent):
 *
 *   source.changes$ → debounce 800 ms ┐
 *   bind, source.focus$ (at once)     ┴→ source.current() → distinct (serialised) → switchMap(render) → state$
 *   refresh() / Retry                    → source.current() ──────────────────────→ switchMap(render)
 *
 * A newer request cancels the one in flight (switchMap); the same request twice is sent once, unless asked again
 * with refresh(). The last render stays on screen while the next one loads, and under an error.
 */
@Injectable()
export class BlockPreviewController implements OnDestroy {
  readonly state$ = new BehaviorSubject<BlockPreviewState>(idleState());

  private readonly forced$ = new Subject<void>();
  private bound: Subscription | null = null;
  private trustedFor: string | null = null;
  private trusted: SafeHtml | null = null;

  constructor(
    private api: BlockPreviewService,
    private sanitizer: DomSanitizer
  ) {}

  /** Follow this source (null: nothing). The first render goes at once. */
  bind(source: BlockPreviewSource | null): void {
    this.bound?.unsubscribe();
    this.bound = null;
    this.state$.next(idleState(source?.hint));
    if (!source) return;
    const asked$ = merge(
      source.changes$.pipe(debounceTime(PREVIEW_DEBOUNCE_MS)),
      source.focus$ ?? EMPTY
    ).pipe(startWith(null));
    const requests$ = asked$.pipe(
      map(() => source.current()),
      distinctUntilChanged((a, b) => requestKey(a) === requestKey(b))
    );
    const again$ = this.forced$.pipe(map(() => source.current()));
    this.bound = merge(requests$, again$)
      .pipe(switchMap((request) => this.render(request, source.hint)))
      .subscribe((state) => this.state$.next(state));
  }

  /** Render what the source holds now, even if it was just rendered (Refresh, Retry). */
  refresh(): void {
    this.forced$.next();
  }

  /**
   * The render as the iframe's srcdoc. Angular would sanitise srcdoc as HTML and strip the site's stylesheet link, so
   * it is marked trusted here, deliberately: the frame is sandbox="" (no scripts, no same-origin, no forms, no
   * popups), the Worker removes every <script> and sets a CSP, and the document has an opaque origin, so it can reach
   * neither Conductor's DOM nor its storage. Memoised so change detection doesn't reload the frame.
   */
  srcdoc(html: string | null): SafeHtml | null {
    if (html !== this.trustedFor) {
      this.trustedFor = html;
      this.trusted = html === null ? null : this.sanitizer.bypassSecurityTrustHtml(html);
    }
    return this.trusted;
  }

  ngOnDestroy(): void {
    this.bound?.unsubscribe();
    this.state$.complete();
  }

  private render(
    request: BlockPreviewRequest | null,
    hint?: string
  ): Observable<BlockPreviewState> {
    if (!request) return of(idleState(hint));
    return this.api.render(request).pipe(
      map(readyState),
      catchError((err: unknown) => of(errorState(this.state$.value, err))),
      startWith(loadingState(this.state$.value))
    );
  }
}
